import { toISODate } from "@inrev/common";
import { DateTime } from "luxon";
import { type FC, type Ref, useEffect, useMemo, useRef, useState } from "react";
import type { DateAfter, DateBefore } from "react-day-picker";
import { FieldError } from "react-hook-form";
import type { RequireAtLeastOne } from "type-fest";
import { Modal } from "../../layout/Modal";
import { cn } from "../../lib/utils";
import { DatePicker, Icon } from "../../primitives";
import { FormFieldError } from "../FormFieldError";
import { inputSize } from "./inputSize";

export type DatePickerInputProps = {
	value?: string;
	defaultMonth?: DateTime;
	placeholder?: string;
	onChange?: (value: string | "") => void;
	onBlur?: () => void;
	className?: string;
	fromDate?: DateTime;
	toDate?: DateTime;
	error?: FieldError;
	errorMessage?: string;
	readonly?: boolean;
	disabled?: boolean;
	disableBefore?: string | null;
	disableAfter?: string | null;
	disableDays?: string[];
	compact?: boolean;
	ref?: Ref<HTMLButtonElement>;
};

const disabledMatchers = ({
	disableBefore,
	disableAfter,
	disableDays,
}: Pick<DatePickerInputProps, "disableBefore" | "disableAfter" | "disableDays">) => {
	const matchers: (RequireAtLeastOne<DateAfter & DateBefore> | Date)[] =
		disableDays?.map((isoDay) => DateTime.fromISO(isoDay).toJSDate()) ?? [];
	if (disableBefore) matchers.push({ before: DateTime.fromISO(disableBefore).toJSDate() });
	if (disableAfter) matchers.push({ after: DateTime.fromISO(disableAfter).toJSDate() });
	return matchers;
};

export const DatePickerInput: FC<DatePickerInputProps> = ({
	value,
	defaultMonth,
	placeholder,
	onChange,
	onBlur,
	className,
	fromDate,
	toDate,
	error,
	errorMessage,
	readonly,
	compact,
	disabled,
	disableBefore,
	disableAfter,
	disableDays,
	ref,
}) => {
	const { frame, gutter } = inputSize(compact);
	const [open, setOpen] = useState<boolean>(false);
	const wasOpenRef = useRef(false);
	const selectedValue = useMemo(() => {
		if (!value) return undefined;
		const parsedDt = DateTime.fromISO(value).toUTC();
		return new Date(parsedDt.year, parsedDt.month - 1, parsedDt.day, 0, 0, 0, 0);
	}, [value]);

	const formattedValue = useMemo(
		() => (value ? DateTime.fromISO(value, { zone: "utc" }).toFormat("MM/dd/yyyy") : ""),
		[value],
	);

	const disabledDays = useMemo(
		() => disabledMatchers({ disableBefore, disableAfter, disableDays }),
		[disableBefore, disableAfter, disableDays],
	);

	useEffect(() => {
		if (wasOpenRef.current && !open) {
			onBlur?.();
		}
		wasOpenRef.current = open;
	}, [open, onBlur]);

	return (
		<>
			<button
				ref={ref}
				type="button"
				className={cn(
					"group flex w-[212px] rounded-md bg-surface-light/70 disabled:bg-surface-neutral outline outline-[1px] outline-offset-[-1px] outline-bc-strong cursor-pointer disabled:cursor-default outline-none focus-visible:ring focus-visible:ring-offset-[3px] focus-visible:ring-brand-accent/30",
					frame,
					disabled ? "cursor-not-allowed opacity-50" : "hover:outline-text-tertiary",
					className,
					readonly ? "!bg-surface-dark/90 hover:outline-bc-strong" : undefined,
					error ? "outline-danger hover:outline-danger relative" : undefined,
				)}
				onClick={() => !readonly && setOpen(true)}
				disabled={readonly || disabled}
				data-testid="date-picker-modal-button"
			>
				<div
					className={cn(
						"h-full flex items-center justify-center rounded-l-md bg-surface-neutral border-r border-r-bc-strong",
						gutter,
					)}
				>
					<Icon
						type="calendar"
						color="secondary"
						size="mini"
						className="group-disabled:text-tertiary"
					/>
				</div>
				<div className="flex-1 flex h-full items-center justify-center">
					{formattedValue !== "" && (
						<span data-testid="date-picker-value" className="text-primary">
							{formattedValue}
						</span>
					)}
					{formattedValue === "" && (
						<span data-testid="date-picker-placeholder" className="text-tertiary/85">
							{placeholder ?? "Select a date"}
						</span>
					)}
				</div>
				<FormFieldError
					error={error}
					errorMessage={errorMessage}
					className={compact ? "left-[26px]" : "left-[46px]"}
				/>
			</button>
			{open && (
				<Modal onClickOutside={() => setOpen(false)} itemClassName="mb-[5%]">
					<DatePicker
						data-testid="date-picker"
						value={selectedValue}
						defaultMonth={defaultMonth?.toJSDate()}
						fromDate={fromDate?.toJSDate()}
						toDate={toDate?.toJSDate()}
						disabled={disabledDays}
						onSelect={(day) => {
							onChange?.(
								day !== undefined
									? (toISODate(DateTime.fromJSDate(day, { zone: "utc" })) ?? "")
									: "",
							);
							setOpen(false);
						}}
					/>
				</Modal>
			)}
		</>
	);
};
